import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Trash2, Target, TrendingUp, Plus } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface KPI {
  id: string;
  name: string;
  description: string;
  category: string;
  metric_type: string;
  target_value: number;
}

interface KPIMapping {
  question_id: string;
  kpi_id: string;
  weight: number;
  mapping_type: string;
}

interface KPIMapperProps {
  surveyQuestions: any[];
  kpiMappings: KPIMapping[];
  onUpdateMappings: (mappings: KPIMapping[]) => void;
}

export function KPIMapper({ surveyQuestions, kpiMappings, onUpdateMappings }: KPIMapperProps) {
  const [kpis, setKpis] = useState<KPI[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadKPIs();
  }, []);

  const loadKPIs = async () => {
    try {
      // Get user's district_id first
      const { data: profile } = await supabase
        .from('profiles')
        .select('district_id')
        .eq('id', (await supabase.auth.getUser()).data.user?.id)
        .single();

      let query = supabase
        .from('district_kpis')
        .select('*')
        .eq('is_active', true);

      if (profile?.district_id) {
        query = query.eq('district_id', profile.district_id);
      }

      const { data, error } = await query.order('category', { ascending: true });

      if (error) throw error;
      setKpis(data || []);
    } catch (error) {
      console.error('Error loading KPIs:', error);
      toast.error('Failed to load KPIs');
    } finally {
      setLoading(false);
    }
  };

  const addMapping = (questionId: string) => {
    const newMapping: KPIMapping = {
      question_id: questionId,
      kpi_id: '',
      weight: 1.0,
      mapping_type: 'direct'
    };
    onUpdateMappings([...kpiMappings, newMapping]);
  };

  const updateMapping = (index: number, field: keyof KPIMapping, value: any) => {
    const updatedMappings = [...kpiMappings];
    updatedMappings[index] = { ...updatedMappings[index], [field]: value };
    onUpdateMappings(updatedMappings);
  };

  const removeMapping = (index: number) => {
    const updatedMappings = kpiMappings.filter((_, i) => i !== index);
    onUpdateMappings(updatedMappings);
  };

  const getKPICategoryColor = (category: string) => {
    const colors = {
      performance: 'bg-blue-100 text-blue-800',
      satisfaction: 'bg-green-100 text-green-800',
      engagement: 'bg-purple-100 text-purple-800',
      effectiveness: 'bg-orange-100 text-orange-800'
    };
    return colors[category as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getMetricTypeIcon = (type: string) => {
    switch (type) {
      case 'scale': return '📊';
      case 'percentage': return '📈';
      case 'numeric': return '🔢';
      case 'boolean': return '✅';
      default: return '📋';
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="text-center text-muted-foreground">Loading KPIs...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            Available KPIs
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {kpis.map((kpi) => (
              <Card key={kpi.id} className="p-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-sm">{kpi.name}</h4>
                    <span className="text-lg">{getMetricTypeIcon(kpi.metric_type)}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{kpi.description}</p>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={getKPICategoryColor(kpi.category)}>
                      {kpi.category}
                    </Badge>
                    {kpi.target_value && (
                      <Badge variant="outline" className="flex items-center gap-1">
                        <TrendingUp className="h-3 w-3" />
                        {kpi.target_value}
                      </Badge>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
          {kpis.length === 0 && (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No KPIs available for your district</p>
              <p className="text-sm text-muted-foreground mt-2">
                Contact your administrator to add district KPIs
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Question-to-KPI Mappings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {surveyQuestions.map((question, questionIndex) => {
            const questionMappings = kpiMappings.filter(m => m.question_id === question.id);
            
            return (
              <Card key={question.id} className="p-4">
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium">Question {questionIndex + 1}</h4>
                    <p className="text-sm text-muted-foreground">{question.question}</p>
                  </div>
                  
                  <div className="space-y-3">
                    {questionMappings.map((mapping, mappingIndex) => {
                      const globalMappingIndex = kpiMappings.findIndex(
                        m => m.question_id === mapping.question_id && 
                        m.kpi_id === mapping.kpi_id
                      );
                      
                      return (
                        <div key={mappingIndex} className="flex items-end gap-3 p-3 bg-muted/50 rounded-lg">
                          <div className="flex-1">
                            <Label className="text-sm">KPI</Label>
                            <Select 
                              value={mapping.kpi_id} 
                              onValueChange={(value) => updateMapping(globalMappingIndex, 'kpi_id', value)}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select a KPI" />
                              </SelectTrigger>
                              <SelectContent>
                                {kpis.map((kpi) => (
                                  <SelectItem key={kpi.id} value={kpi.id}>
                                    <div className="flex items-center gap-2">
                                      <span>{getMetricTypeIcon(kpi.metric_type)}</span>
                                      <span>{kpi.name}</span>
                                      <Badge variant="outline" className="text-xs">
                                        {kpi.category}
                                      </Badge>
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="w-24">
                            <Label className="text-sm">Weight</Label>
                            <Input
                              type="number"
                              min="0"
                              max="10"
                              step="0.1"
                              value={mapping.weight}
                              onChange={(e) => updateMapping(globalMappingIndex, 'weight', parseFloat(e.target.value) || 1)}
                            />
                          </div>

                          <div className="w-32">
                            <Label className="text-sm">Type</Label>
                            <Select 
                              value={mapping.mapping_type} 
                              onValueChange={(value) => updateMapping(globalMappingIndex, 'mapping_type', value)}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="direct">Direct</SelectItem>
                                <SelectItem value="calculated">Calculated</SelectItem>
                                <SelectItem value="aggregated">Aggregated</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeMapping(globalMappingIndex)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      );
                    })}
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => addMapping(question.id)}
                      className="flex items-center gap-2"
                    >
                      <Plus className="h-4 w-4" />
                      Map to KPI
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
          
          {surveyQuestions.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <p>Add questions to your survey first to map them to KPIs</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}